import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { Widget } from './Widget';
import type { WidgetPreferences } from '../hooks/useWidgetPreferences';
import { useI18n } from '../contexts/I18nContext';

interface WidgetGridProps {
  widgets: Array<{
    id: string;
    title: string;
    content: React.ReactNode;
  }>;
  preferences: WidgetPreferences;
  isLoading?: boolean;
  editable?: boolean;
  columns?: number;
  onReorder: (widgetIds: string[]) => void;
  onToggleVisibility: (widgetId: string, visible: boolean) => void;
  getVisibleWidgets: () => Array<{ id: string; visible: boolean; order: number }>;
}

export const WidgetGrid: React.FC<WidgetGridProps> = ({
  widgets,
  preferences,
  isLoading = false,
  editable = false,
  columns = 4,
  onReorder,
  onToggleVisibility,
  getVisibleWidgets,
}) => {
  const { t } = useI18n();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const visibleWidgets = getVisibleWidgets();
  const widgetMap = new Map(widgets.map(w => [w.id, w]));

  const orderedWidgets = visibleWidgets
    .map(config => widgetMap.get(config.id))
    .filter(Boolean) as Array<{
    id: string;
    title: string;
    content: React.ReactNode;
  }>;

  const handleDragEnd = (event: DragEndEvent) => {
    if (!editable) return;
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = orderedWidgets.findIndex(w => w.id === active.id);
      const newIndex = orderedWidgets.findIndex(w => w.id === over.id);

      const newOrder = arrayMove(orderedWidgets, oldIndex, newIndex).map(w => w.id);
      onReorder(newOrder);
    }
  };

  const gridTemplateColumns = `repeat(${columns}, 1fr)`;
  const isGridLayout = preferences.layout === 'grid';

  if (!isLoading && orderedWidgets.length === 0) {
    return (
      <div
        style={{
          padding: '24px',
          textAlign: 'center',
          color: '#666',
          backgroundColor: 'white',
          borderRadius: '8px',
          marginBottom: '30px',
        }}
      >
        {t('dashboardEmptyWidgets')}
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={orderedWidgets.map(w => w.id)} strategy={rectSortingStrategy}>
        <div
          style={{
            display: isGridLayout ? 'grid' : 'flex',
            flexDirection: isGridLayout ? undefined : 'column',
            gridTemplateColumns: isGridLayout ? gridTemplateColumns : undefined,
            gap: '20px',
            marginBottom: '30px',
            opacity: isLoading ? 0.6 : 1,
          }}
        >
          {orderedWidgets.map(widget => {
            const config = preferences.widgets.find(w => w.id === widget.id);
            return (
              <Widget
                key={widget.id}
                id={widget.id}
                title={widget.title}
                visible={config?.visible ?? true}
                showControls={editable}
                highlightEdit={editable}
                onToggleVisibility={() => {
                  onToggleVisibility(widget.id, !(config?.visible ?? true));
                }}
              >
                {widget.content}
              </Widget>
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );
};
